import { LONG_OVERDUE_DAYS, PROJECT_STATUSES, PROJECT_STATUS_LABELS, attentionItems, credentialStats, daysOverdue, lastCall, missedWeeks, nextCallOn, openProjectActions, todayIso } from '../../../shared/projects.js'
import { ProjectActions } from './ProjectActions.jsx'
import { CallsTab } from './CallsTab.jsx'
import { SybillTab } from './SybillTab.jsx'
import { CommitInput, fmtDate, fmtDateYear, projectPill } from './fields.jsx'
import { SystemsTab } from './SystemsTab.jsx'
import styles from './projects.module.css'

/** One project: header, four tabs (the tab lives in the URL). */
export function ProjectView({ project, tab, onTab, api, canEdit }) {
  const today = todayIso()
  const stats = credentialStats(project)
  const missed = missedWeeks(project, today)
  const current = ['overview', 'systems', 'calls', 'sybill'].includes(tab) ? tab : 'overview'

  const tabs = [
    ['overview', 'Overview', null],
    ['systems', 'Systems & credentials', <span key="c" className={styles.count}>{`${stats.received}/${stats.total}`}</span>],
    ['calls', 'Weekly calls', missed ? <span key="c" className={`${styles.count} ${styles.countBad}`}>{`${missed} missed`}</span> : <span key="c" className={styles.count}>{project.calls.length}</span>],
    ['sybill', 'Sybill', null],
  ]

  return (
    <>
      <div className={styles.head}>
        <div>
          <h1 className={styles.title}>{project.name}</h1>
          <p className={styles.sub}>
            {[project.client, project.owner || null, `started ${fmtDateYear(project.startOn)}`].filter(Boolean).join(' · ')}
          </p>
        </div>
        <span className={projectPill(project.status)}>{PROJECT_STATUS_LABELS[project.status]}</span>
      </div>

      <div className={styles.tabs} role="tablist">
        {tabs.map(([key, label, badge]) => (
          <button key={key} type="button" role="tab" aria-selected={current === key} className={`${styles.tab} ${current === key ? styles.tabOn : ''}`} onClick={() => onTab(key)}>
            {label}
            {badge}
          </button>
        ))}
      </div>

      {current === 'overview' ? <Overview project={project} onTab={onTab} api={api} canEdit={canEdit} stats={stats} missed={missed} today={today} /> : null}
      {current === 'systems' ? <SystemsTab project={project} api={api} canEdit={canEdit} /> : null}
      {current === 'calls' ? <CallsTab project={project} api={api} canEdit={canEdit} /> : null}
      {current === 'sybill' ? <SybillTab project={project} api={api} canEdit={canEdit} /> : null}
    </>
  )
}

function Overview({ project, onTab, api, canEdit, stats, missed, today }) {
  const last = lastCall(project)
  const next = nextCallOn(project)
  const openList = openProjectActions(project)
  const open = openList.length
  const longOverdue = openList.filter((a) => daysOverdue(a.dueOn, today) > LONG_OVERDUE_DAYS).length
  const items = attentionItems(project, today)
  const pct = stats.total ? Math.round((stats.received / stats.total) * 100) : 0
  const save = (patch) => api.patch(project.id, patch)

  return (
    <>
      <div className={styles.tiles}>
        <div className={styles.tile}>
          <b>Credentials</b>
          <span>
            {stats.received}/{stats.total}
          </span>
          <small>{pct}% received</small>
        </div>
        <div className={styles.tile}>
          <b>Last call</b>
          <span>{last ? fmtDate(last.heldOn) : '–'}</span>
          <small>{last ? last.summary || 'No summary' : 'No call logged'}</small>
        </div>
        <div className={styles.tile}>
          <b>Next call</b>
          <span>{next ? fmtDate(next) : '–'}</span>
          <small>{next ? '' : 'Set it in Weekly calls'}</small>
        </div>
        <div className={styles.tile}>
          <b>Open actions</b>
          <span>{open}</span>
          <small>{longOverdue ? `${longOverdue} long overdue` : missed ? `${missed} missed week${missed > 1 ? 's' : ''}` : 'No missed weeks'}</small>
        </div>
      </div>

      <div className={styles.two}>
        <section className={styles.panel}>
          <h2 className={styles.h}>Needs attention</h2>
          {items.length ? (
            <ul className={`${styles.attn} ${styles.attnScroll}`} tabIndex={0} aria-label="Items that need attention">
              {items.map((item, i) => (
                <li key={i} className={item.tone === 'bad' ? styles.bad : undefined}>
                  <span>{item.text}</span>
                  {item.kind === 'action' ? null : (
                    <button type="button" className={`${styles.btn} ${styles.sm}`} onClick={() => onTab(item.tab)}>
                      Open
                    </button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className={styles.ok}>Nothing is blocked or overdue.</p>
          )}
        </section>

        <section className={styles.panel}>
          <h2 className={styles.h}>Project details</h2>
          <div className={styles.formOne}>
            <label className={styles.fieldLabel}>
              Name
              <CommitInput value={project.name} disabled={!canEdit} maxLength={120} onCommit={(v) => v.trim().length >= 2 && save({ name: v })} />
            </label>
            <label className={styles.fieldLabel}>
              Client
              <CommitInput value={project.client} disabled={!canEdit} maxLength={120} onCommit={(v) => v.trim() && save({ client: v })} />
            </label>
            <label className={styles.fieldLabel}>
              Owner
              <CommitInput value={project.owner} disabled={!canEdit} maxLength={80} onCommit={(v) => save({ owner: v })} />
            </label>
            <label className={styles.fieldLabel}>
              Status
              <select className={styles.select} value={project.status} disabled={!canEdit} onChange={(e) => save({ status: e.target.value })}>
                {PROJECT_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {PROJECT_STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.fieldLabel}>
              Target go-live
              <CommitInput type="date" value={project.goLiveOn ?? ''} disabled={!canEdit} onCommit={(v) => save({ goLiveOn: v || null })} />
            </label>
          </div>
        </section>
      </div>

      <ProjectActions project={project} api={api} canEdit={canEdit} />
    </>
  )
}
